import { useMemo, useEffect, useState } from "react";

import { getUnixTs, roundAutoToDecimals } from "@bridgesplit/utils";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useMemoizedKeyMap } from "@bridgesplit/ui";
import { useUserPublicKey } from "@bridgesplit/react";

import {
    usePointMultipliersQuery,
    useRewardsQuery,
    useUserPointsQuery,
    useUserReferralInfoQuery,
    usePointsTotalForRewardQuery,
    useUserReferralPointsQuery,
    usePortfolioRewardsQuery,
    useAuth
} from "../reducers";
import {
    LoopscalePointsAction,
    PointsRewardType,
    PortfolioRewardsFilter,
    UserPointsCalculationInfo,
    StrategyRewards,
    LedgerReward,
    VaultReward,
    RewardPointsPerSecond,
    LoanMultiplier,
    LoanMultiplierInfo,
    BreakdownActivity,
    PortfolioRewardsBreakdown
} from "../types";
import { BASE_PPS, BASE_REFERRAL_PPS } from "../constants";
import { useActiveWallet } from "./wallet";
import { useAbfTypesToUiConverter } from "../utils";
import { useUser } from "./auth";

export function useSkipPoints() {
    const { activeWallet } = useActiveWallet();

    return useMemo(() => !activeWallet, [activeWallet]);
}

export function useUserId() {
    const { data: user } = useUser();
    return user?.wallet ?? skipToken;
}

export function useUserMeParams() {
    const { isAuthenticated, isWalletBased, isLoading } = useAuth();
    const userPubkey = useUserPublicKey();
    const params = isWalletBased ? { walletPubkey: userPubkey } : undefined;
    const skip = !isAuthenticated || isLoading;

    return { params, skip, isLoading };
}

export function useRewards() {
    const { data, isLoading } = useRewardsQuery({ feVisible: true, activeOnly: true });

    const rewardToInfo = useMemoizedKeyMap(data, (t) => t.reward);

    function getRewardInfo(type: PointsRewardType) {
        return rewardToInfo?.get(type);
    }

    return { data, getRewardInfo, isLoading };
}

export function useUserReferral(options?: { skip?: boolean }) {
    const userId = useUserId();
    const skip = useSkipPoints();

    return useUserReferralInfoQuery(userId, {
        skip: skip || !!options?.skip
    });
}

export type UserPointsFunction = ReturnType<typeof useUserPoints>["getPointsAtTime"];
export function useUserPoints(options?: { skip?: number }) {
    // await for referral info or invite code endpoints before calling points to catch point creation endpoints
    const { activeWallet } = useActiveWallet();
    const userId = useUserId();

    const {
        data: basePoints,
        isLoading,
        isUninitialized
    } = useUserPointsQuery(userId, { skip: !activeWallet || !!options?.skip });

    const {
        data: referralPoints,
        isLoading: referralsLoading,
        isUninitialized: referralsUninitialized
    } = useUserReferralPointsQuery(userId, { skip: !activeWallet || !!options?.skip });

    const pointsData: UserPointsCalculationInfo | undefined = basePoints &&
        referralPoints && {
            basePoints,
            referralPoints
        };

    return {
        getPointsAtTime: (timestamp: number) => getPointsAtTime(pointsData, timestamp),
        isLoading: isUninitialized || isLoading || referralsLoading || referralsUninitialized
    };
}

export function usePortfolioRewards({
    portfolioRewardsFilter,
    skip = false
}: {
    portfolioRewardsFilter?: Partial<PortfolioRewardsFilter>;
    skip?: boolean;
}) {
    const { activeWallet } = useActiveWallet();

    const filter: PortfolioRewardsFilter = {
        loanAddresses: portfolioRewardsFilter?.loanAddresses ?? [],
        strategyAddresses: portfolioRewardsFilter?.strategyAddresses ?? [],
        stakeAddresses: portfolioRewardsFilter?.stakeAddresses ?? [],
        isActive: portfolioRewardsFilter?.isActive ?? undefined
    };

    const {
        data: portfolioRewards,
        isLoading,
        isFetching
    } = usePortfolioRewardsQuery(filter, {
        skip: skip || !activeWallet
    });

    const portfolioData = useMemo(() => {
        // Don't return any data if we're fetching to prevent showing stale data
        if (isFetching || isLoading || !activeWallet || skip) return undefined;
        if (!portfolioRewards) {
            return {
                strategies: {},
                loans: {},
                vaultStakes: {},
                loops: {}
            };
        }
        return portfolioRewards;
    }, [isFetching, isLoading, activeWallet, skip, portfolioRewards]);

    // Consider the data as loading if either initial loading or refetching is happening
    return { data: portfolioData, isLoading: isLoading || isFetching };
}

function getPointsAtTime(userInfo: UserPointsCalculationInfo | undefined, now: number) {
    if (!userInfo?.basePoints || !userInfo?.referralPoints) {
        return undefined;
    }

    const { basePoints: rawBase, referralPoints: rawReferral } = userInfo;

    // Add null checks with default 0
    const basePPS = rawBase.pps ?? BASE_PPS;
    const referralPPS = rawReferral.pps ?? BASE_REFERRAL_PPS;

    const baseSecondsElapsed = Math.max(now - rawBase.timestamp, 0);
    const basePoints = rawBase.points + basePPS * baseSecondsElapsed;

    const referralSecondsElapsed = Math.max(now - rawReferral.timestamp, 0);
    const referralPoints = rawReferral.points + referralPPS * referralSecondsElapsed;

    // Beta Points
    const betaPoints = rawBase.betaPoints ?? 0;

    // Total Points
    const totalPoints = basePoints + referralPoints + betaPoints;

    return { totalPoints, referralPoints, betaPoints };
}

export function useCurrentTime(pollingInterval = 50) {
    const [now, setNow] = useState<number>(getUnixTs());

    useEffect(() => {
        if (!pollingInterval) return;
        const interval = setInterval(() => {
            setNow(getUnixTs());
        }, pollingInterval);

        return () => clearInterval(interval);
    }, [pollingInterval]);

    return { now };
}

export function usePointsSumForReward(pointsReward: PointsRewardType) {
    const { getRewardInfo, isLoading: rewardLoading } = useRewards();
    const userId = useUserId();

    const { data: totalMultiplier, isLoading: multiplierLoading } = usePointsTotalForRewardQuery(
        typeof userId === "string"
            ? {
                  pointsReward,
                  userId
              }
            : skipToken
    );

    const totalPoints = useMemo(() => {
        const rewardInfo = getRewardInfo(pointsReward);
        if (!rewardInfo || !totalMultiplier) return 0;
        return rewardInfo.pointsPerSecond * totalMultiplier;
    }, [getRewardInfo, pointsReward, totalMultiplier]);

    return { totalPoints, isLoading: rewardLoading || multiplierLoading };
}

function useLoanMultipliers(skip?: boolean) {
    const { data: loanMultipliers, isLoading } = usePointMultipliersQuery(undefined, { skip });

    const loanMultiplier = useMemo(() => {
        if (!loanMultipliers) {
            return undefined;
        }

        // Group multipliers by mint
        const loanMultiplier = loanMultipliers.reduce<LoanMultiplier>((acc, multiplier) => {
            const { mint, name, reward, multiplier: value } = multiplier;

            if (!acc[mint]) {
                acc[mint] = {} as Record<LoopscalePointsAction, LoanMultiplierInfo>;
            }

            acc[mint][reward] = {
                name,
                multiplier: value
            };

            return acc;
        }, {});

        return loanMultiplier;
    }, [loanMultipliers]);

    return { loanMultipliers: loanMultiplier, isLoading };
}
export function useLoanMultipliersUtil(skip?: boolean) {
    const { loanMultipliers, isLoading } = useLoanMultipliers(skip);

    return { loanMultipliers, isLoading };
}

export function useRewardsPointsPerSecond() {
    const { data: rewards, isLoading } = useRewards();

    const rewardPointsPerSecond = useMemo(() => {
        if (!rewards) return {} as RewardPointsPerSecond;

        const pointsPerSecondMap = rewards.reduce<Record<LoopscalePointsAction, number>>((acc, reward) => {
            const rewardValue = reward.reward as LoopscalePointsAction;
            if (Object.values(LoopscalePointsAction).includes(rewardValue)) {
                acc[rewardValue] = reward.pointsPerSecond;
            }
            return acc;
        }, {} as Record<LoopscalePointsAction, number>);

        const idleLendPps = pointsPerSecondMap[LoopscalePointsAction.IdleCap] || 1;

        return rewards.reduce<RewardPointsPerSecond>((acc, reward) => {
            const rewardValue = reward.reward as LoopscalePointsAction;
            if (Object.values(LoopscalePointsAction).includes(rewardValue)) {
                acc[rewardValue] = {
                    pointsPerSecond: reward.pointsPerSecond,
                    relativePointsPerSecond: roundAutoToDecimals(reward.pointsPerSecond / idleLendPps, 0),
                    rewardLabel: reward.feRewardLabel
                };
            }
            return acc;
        }, {} as RewardPointsPerSecond);
    }, [rewards]);

    return { rewardPointsPerSecond, isLoading };
}

// Returns all portfolio rewards for active user positions. Used for daily points accrual calculations
export function useActivePortfolioRewards(skip?: boolean) {
    const portfolioRewards = usePortfolioRewards({ portfolioRewardsFilter: { isActive: true }, skip });
    return portfolioRewards;
}

// Takes all active portfolio rewards and returns a breakdown
export function useRewardsBreakdown() {
    const { data: portfolioRewards, isLoading: portfolioRewardsLoading } = useHistoricalPortfolioRewards();
    const { rewardPointsPerSecond, isLoading: rewardPointsPerSecondLoading } = useRewardsPointsPerSecond();

    const { convertPortfolioRewards } = useAbfTypesToUiConverter([]);

    const data = convertPortfolioRewards(portfolioRewards, rewardPointsPerSecond);

    const isLoading = portfolioRewardsLoading || rewardPointsPerSecondLoading;

    // Check if portfolioRewards is undefined or empty (no strategies, loans, vaultStakes, or loops)
    const isEmpty =
        !portfolioRewards ||
        (Object.keys(portfolioRewards.strategies).length === 0 &&
            Object.keys(portfolioRewards.loans).length === 0 &&
            Object.keys(portfolioRewards.vaultStakes).length === 0 &&
            Object.keys(portfolioRewards.loops).length === 0);

    if ((isEmpty || !rewardPointsPerSecond) && !isLoading) {
        return { data: [], isLoading };
    }

    // Group idle lend rewards by vault name
    // Create a map of vault names to their aggregated rewards for faster lookup
    const groupedData = getSimplePortfolioBreakdown(data);

    return { data: isLoading ? undefined : groupedData, isLoading };
}
/**
 * Simplifies portfolio breakdown by aggregating rewards for the same vault
 * Vaults with the same name will have their rewards combined into a single entry
 * Active stakes are displayed individually, while inactive stakes are aggregated
 * @param data Array of portfolio rewards breakdown
 * @returns Simplified array with aggregated inactive vault rewards
 */
function getSimplePortfolioBreakdown(data: PortfolioRewardsBreakdown[]): PortfolioRewardsBreakdown[] {
    const inactiveVaultMap: Record<string, PortfolioRewardsBreakdown> = {};
    const nonAggregatedRewards: PortfolioRewardsBreakdown[] = [];

    const isVaultReward = (reward: PortfolioRewardsBreakdown): boolean =>
        reward.activity === BreakdownActivity.LendVault;

    // TODO: Makeshift isActive check since endTime still considered rewards active
    const isActive = (reward: PortfolioRewardsBreakdown): boolean => reward.pointsPerDay > 0;

    for (const reward of data) {
        if (isVaultReward(reward) && reward.vaultMetadata) {
            const vaultName = reward.vaultMetadata.name;

            if (isActive(reward)) {
                // Keep active vault stakes as individual rows
                nonAggregatedRewards.push(reward);
            } else {
                // Aggregate inactive vault stakes by vault name
                if (inactiveVaultMap[vaultName]) {
                    // Update existing inactive vault entry
                    const existing = inactiveVaultMap[vaultName];

                    // Get existing and new boost values, defaulting to 1 if undefined
                    const existingBoost = existing.boost || 1;
                    const currentRewardBoost = reward.boost || 1;

                    // For inactive vaults, use totalPoints as a weight factor
                    const existingRewardTotalPoints = existing.totalPoints || 0;
                    const currentRewardTotalPoints = reward.totalPoints || 0;
                    const totalPoints = existingRewardTotalPoints + currentRewardTotalPoints;

                    // Calculate weighted boost based on totalPoints
                    const weightedBoost =
                        totalPoints > 0
                            ? (existingBoost * existingRewardTotalPoints) / totalPoints +
                              (currentRewardBoost * currentRewardTotalPoints) / totalPoints
                            : Math.max(existingBoost, currentRewardBoost);

                    inactiveVaultMap[vaultName] = {
                        ...existing,
                        totalPoints,
                        boost: weightedBoost
                    };
                } else {
                    inactiveVaultMap[vaultName] = reward;
                }
            }
        } else {
            // Keep non-vault rewards as is
            nonAggregatedRewards.push(reward);
        }
    }

    // Combine non-aggregated rewards with aggregated inactive vault rewards
    return [...nonAggregatedRewards, ...Object.values(inactiveVaultMap)];
}

// Returns all portfolio rewards including non active positions
export function useHistoricalPortfolioRewards(skip?: boolean) {
    const portfolioRewards = usePortfolioRewards({ portfolioRewardsFilter: {}, skip });
    return portfolioRewards;
}

// Custom hook to get portfolio rewards by loan addresses. Used for loan cards
export function usePortfolioRewardsByLoans(loanAddresses?: string[]) {
    const filter = loanAddresses?.length
        ? {
              loanAddresses,
              strategyAddresses: [],
              stakeAddresses: [],
              isActive: true
          }
        : undefined;

    return usePortfolioRewards({ portfolioRewardsFilter: filter });
}

export function usePortfolioRewardsUtils() {
    const { data: portfolioRewards } = useActivePortfolioRewards();

    /**
     * Returns reward data for a specific loan
     * @param loanAddress The address of the loan to check
     * @returns The reward data for the loan, or undefined if not found
     */
    const getLoanRewards = (loanAddress: string | undefined): LedgerReward[] | undefined => {
        const loanRewards = loanAddress ? portfolioRewards?.loans?.[loanAddress] : undefined;
        return loanRewards;
    };

    const getLoopRewards = (loopAddress: string | undefined): LedgerReward[] | undefined => {
        const loopRewards = loopAddress ? portfolioRewards?.loops?.[loopAddress] : undefined;
        return loopRewards;
    };

    /**
     * Returns reward data for a specific strategy
     * @param strategyAddress The address of the strategy to check
     * @returns The reward data for the strategy, or undefined if not found
     */
    const getStrategyRewards = (strategyAddress: string | undefined): StrategyRewards | undefined => {
        const strategyReward = strategyAddress ? portfolioRewards?.strategies?.[strategyAddress] : undefined;
        return strategyReward;
    };

    /**
     * Returns reward data for a specific stake
     * @param stakeAddress The address of the stake to check
     * @returns The reward data for the stake, or undefined if not found
     */
    const getStakeRewards = (stakeAddress: string | undefined): VaultReward | undefined => {
        const stakeRewards = stakeAddress ? portfolioRewards?.vaultStakes?.[stakeAddress] : undefined;
        return stakeRewards;
    };

    return {
        getLoanRewards,
        getLoopRewards,
        getStrategyRewards,
        getStakeRewards,
        isLoading: !portfolioRewards
    };
}
